import { getName } from '@home/utils/common';
import type {
  ICardMetricItem,
  IFilterOverrideConfig,
  TFilterVal,
} from './kpiCardUtils.types';

// 兼容原有调用方：继续通过本文件导出类型（类型定义已抽到 ./kpiCardUtils.types.ts）
export type { ICardMetricItem, IFilterOverrideConfig };

export const MONTH_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const getMetricValue = (
  metricsMap: Record<string, ICardMetricItem> | undefined,
  metricName: string,
): string => {
  return metricsMap?.[metricName]?.value || '';
};

export const calcPercent = (actual: string, target: string): number => {
  const a = parseFloat(actual);
  const t = parseFloat(target);
  if (isNaN(a) || isNaN(t) || t === 0) return 0;
  return (a / t) * 100;
};

export const getDateRangeText = (filterVal: TFilterVal | undefined): string => {
  const rt = filterVal?.rangeTime || [];
  const end = rt[1] || rt[0] || '';
  if (!end) return '';
  const parts = end.split('-');
  if (parts.length < 2) return '';
  const monthFirst = `${parts[0]}-${parts[1]}-01`;
  return `${monthFirst}~${end}`;
};

export const getMonthFirstDate = (filterVal: TFilterVal | undefined): string => {
  const rt = filterVal?.rangeTime || [];
  const end = rt[1] || rt[0] || '';
  if (!end) return '';
  const parts = end.split('-');
  if (parts.length < 2) return '';
  return `${parts[0]}-${parts[1]}-01`;
};

export const getEndDate = (filterVal: TFilterVal | undefined): string => {
  const rt = filterVal?.rangeTime || [];
  return rt[1] || rt[0] || '';
};

export const getMonthTargetLabel = (
  filterVal: TFilterVal | undefined,
  isEN: boolean,
  overrideCn?: string,
  overrideEn?: string,
): string => {
  if (overrideCn || overrideEn) {
    const label = getName(isEN, overrideEn, overrideCn);
    if (label) return label;
  }
  const rt = filterVal?.rangeTime || [];
  const end = rt[1] || rt[0] || '';
  if (!end) return isEN ? 'Target' : '目标';
  const month = end.split('-')[1];
  if (!month) return isEN ? 'Target' : '目标';
  const monthNum = parseInt(month, 10);
  return isEN ? `${MONTH_EN[monthNum - 1] || ''} Target` : `${monthNum}月目标`;
};

export const resolveDataKey = (
  data: Record<string, unknown> | undefined,
  explicitDataKey?: string,
): string => {
  if (explicitDataKey && data?.[explicitDataKey]) return explicitDataKey;
  const keys = Object.keys(data || {}).filter(
    k => k !== 'filter' && typeof data?.[k] === 'object' && (data?.[k] as { data?: unknown; })?.data,
  );
  return keys[0] || '';
};

// value 可能为数组、对象或 undefined（filterVal 某维度值），用 unknown 入口，内部做结构化守卫再访问
const getFilterList = (
  key: string,
  value: unknown,
): unknown[] | undefined => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (key === 'region') return obj.markets as unknown[] | undefined;
    return obj[key] as unknown[] | undefined;
  }
  return undefined;
};

const getFilterValuesByKey = (
  filterVal: TFilterVal = {},
  filterKey: string,
): string[] => {
  if (filterKey === 'market') {
    const regionValues = getFilterList('region', filterVal.region);
    return (regionValues || []).map(v => String(v));
  }
  const values = getFilterList(filterKey, filterVal[filterKey]);
  return (values || []).map(v => String(v));
};

const isAllSelectValue = (
  values: string[],
  allValue = '255',
): boolean => (
  values.length === 0 ||
  (values.length === 1 && String(values[0]) === allValue)
);

export const isFilterOverridesMatched = (
  filterVal: TFilterVal = {},
  overrides: IFilterOverrideConfig[] = [],
): boolean => {
  if (!overrides.length) return true;
  return overrides.every(override => {
    const defaultValues = (override.values || []).map(v => String(v));
    if (!defaultValues.length) return false;
    const userValues = getFilterValuesByKey(filterVal, override.filterKey);
    if (isAllSelectValue(userValues)) {
      return true;
    }
    const defaultSet = new Set(defaultValues);
    return userValues.some(v => defaultSet.has(v));
  });
};
